import { Injectable } from '@angular/core';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { Email, EmailService } from 'src/app/services/email.service';

// The mails an extra delivery generates. Extracted from NewDeliveryDialogComponent
// so the approval flow can send exactly the same ones: a portal order is mailed
// to logistics only when a Frigg admin approves it, and that mail must be
// identical to the one an operator-created order produces — logistics reads them
// side by side.
//
// `settings` is the stocking settings doc (logisticsEmail, ilEmail).
@Injectable({ providedIn: 'root' })
export class DeliveryEmailService {

  constructor(private emailService: EmailService) { }

  private facts(order: any): string {
    const systemHtml = order.systemName ? `<p><strong>System:</strong> ${order.systemName}</p>` : '';
    return `
              <p><strong>Delivery Number:</strong> ${order.deliveryNumber}</p>
               <p><strong>Type:</strong> ${order.typeDelivery}</p>
               <p><strong>Date:</strong> ${moment(order.date).format("dddd, DD.MM.YYYY")}</p>
               ${systemHtml}
               <p><strong>Description:</strong> ${order.desciption}</p>
               <p><strong>Comment:</strong> ${order.comment}</p>
               <p><strong>Products:</strong></p>
               <ul>${order.products.map(p => `<li>${p.amount} x ${p.product}</li>`).join('')}</ul>`;
  }

  // Goes out at 08:00 the day before, so it is scheduled rather than sent now.
  // An update replaces the queued mail instead of adding a second one.
  sendLogisticsEmail(order: any, settings: any, update: boolean) {
    const sendAtMoment = moment(order.date).subtract(1, 'days').hour(8).minute(0).second(0);
    const updatedSubject = update ? `(Updated)` : '';

    const logsticsEmail: Email = {
      emailTo: settings.logisticsEmail,
      cc: order.email,
      subject: `${updatedSubject}Logistics Extra Delivery #${order.deliveryNumber} ${order.systemName ? ' - ' + order.systemName : ''} (Do not reply)`,
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: `
                <p><strong>New delivery:</strong></p>
                <p><strong>Creator:</strong> ${order.name} (${order.email})</p>
                ${this.facts(order)}

               <p><strong>This mail is automatically sendt, please do not reply.</strong> </p>`,
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      sendAtUnix: sendAtMoment.unix(),
      sendAt: sendAtMoment.format("YYYY-MM-DD HH:mm:ss"),
      asap: sendAtMoment.isBefore(moment())
    };

    if (update) {
      return this.emailService.updateEmail(order.logsticsEmailRef, logsticsEmail);
    }
    return this.emailService.sendEmail(logsticsEmail);
  }

  // Only food that is made fresh in the morning concerns IL, which is why this
  // resolves to null when the order has no baguettes.
  sendIlEmail(order: any, settings: any, update: boolean) {
    const productsForFreshDelivery = order.products.filter((product) => {
      return typeof product.productType == 'string' && product.productType.includes('BAGUETTE');
    });

    const updatedSubject = update ? `(Updated)` : '';

    if (!productsForFreshDelivery.length) return Promise.resolve(null);

    const ilEmail: Email = {
      emailTo: settings.ilEmail,
      cc: order.email,
      // The operator flow only reaches here for a sales meeting, so this reads
      // exactly as it always did there — and a portal order says what it is
      // instead of calling itself a sales meeting.
      subject: `${updatedSubject}New Extra Delivery #${order.deliveryNumber} - ${order.typeDelivery} (Do not reply)`,
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: `
               <p>Food to be prepared fresh for pickup at 09:30</p>
               <p><strong>Type:</strong> ${order.typeDelivery}</p>
               <p><strong>Date:</strong> ${moment(order.date).format("dddd, DD.MM.YYYY")} 09:30</p>
               <p><strong>Description:</strong> ${order.desciption}</p>
               <p><strong>Comment:</strong> ${order.comment}</p>
               <p><strong>Products:</strong></p>
               <ul>${productsForFreshDelivery.map(p => `<li>${p.amount} x ${p.product}</li>`).join('')}</ul>

               <p><strong>This mail is automatically sendt, please do not reply.</strong> </p>
               <p><strong>Confirmation to be sendt to: ${order.email}</strong> </p>`,
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      asap: true,
    };

    return this.emailService.sendEmail(ilEmail);
  }

  sendIlKickoffEmail(order: any, settings: any, update: boolean) {
    const updatedSubject = update ? `(Updated)` : '';

    const ilEmail: Email = {
      emailTo: settings.ilEmail,
      cc: order.email,
      subject: `${updatedSubject}New Extra Delivery #${order.deliveryNumber} - Kick-off (Do not reply)`,
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: `
               <p>Food for Kick Off!</p>
               <p><strong>Type:</strong> ${order.typeDelivery}</p>
               <p><strong>Date:</strong> ${moment(order.date).format("dddd, DD.MM.YYYY")}</p>
               <p><strong>Description:</strong> ${order.desciption}</p>
               <p><strong>Comment:</strong> ${order.comment}</p>
               <p><strong>Products:</strong></p>
               <ul>${order.products.map(p => `<li>${p.amount} x ${p.product}</li>`).join('')}</ul>

               <p><strong>This mail is automatically sendt, please do not reply.</strong> </p>
               <p><strong>Confirmation to be sendt to: ${order.email}</strong> </p>`,
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      asap: true,
    };

    return this.emailService.sendEmail(ilEmail);
  }

  orderConfirmationEmail(order: any, update: boolean) {
    const updatedSubject = update ? `(Updated)` : '';

    const orderConfirmEmail: Email = {
      emailTo: order.email,
      subject: `${updatedSubject}Order confimation Extra Delivery #${order.deliveryNumber} ${order.systemName ? ' - ' + order.systemName : ''} (Do not reply)`,
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: `
                <p><strong>If you orded food to be prepared in the morning you need separate confirmation from IL.</strong></p><br>
                <p><strong>New delivery:</strong></p>
                <p><strong>Creator:</strong> ${order.name} (${order.email})</p>
                ${this.facts(order)}

               <p><strong>This mail is automatically sendt, please do not reply.</strong> </p>`,
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      asap: true,
    };

    return this.emailService.sendEmail(orderConfirmEmail);
  }

  // ---- portal orders ---------------------------------------------------------
  //
  // Møtemat mail is Norwegian first, then English, with the order facts shown
  // once under two-language labels — the same layout as the portal's own mails
  // (friggkantine-cloudfunctions services/cateringService.js bilingualHtml).
  // The logistics/IL mails above stay as they are: operator orders share them.

  private static readonly WEEKDAYS_NO = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
  private static readonly WEEKDAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  private bilingualSubject(statusNo: string, statusEn: string, order: any, withSystem: boolean): string {
    const system = withSystem && order.systemName ? ` - ${order.systemName}` : '';
    return `${statusNo} / ${statusEn}: møtemat / meeting food #${order.deliveryNumber}${system} (Ikke svar / Do not reply)`;
  }

  private bilingualHtml(order: any, no: string, en: string, extra: { total?: boolean, reason?: string } = {}): string {
    const m = moment(order.date);
    const day = `${DeliveryEmailService.WEEKDAYS_NO[m.day()]} / ${DeliveryEmailService.WEEKDAYS_EN[m.day()]}`;
    const divider = '<hr style="border:none;border-top:1px solid #ddd;margin:20px 0">';
    return `
        <div lang="nb">${no}</div>
        ${divider}
        <div lang="en">${en}</div>
        ${divider}
        ${extra.reason !== undefined ? `<p><strong>Begrunnelse / Reason:</strong> ${extra.reason || 'Ingen begrunnelse oppgitt / No reason given'}</p>` : ''}
        <p><strong>Leveringsnummer / Delivery number:</strong> ${order.deliveryNumber}</p>
        <p><strong>Type:</strong> ${order.typeDelivery}</p>
        <p><strong>Dato / Date:</strong> ${day}, ${m.format('DD.MM.YYYY')}</p>
        ${order.systemName ? `<p><strong>System:</strong> ${order.systemName}</p>` : ''}
        <p><strong>Beskrivelse / Description:</strong> ${order.desciption || ''}</p>
        <p><strong>Kommentar / Comment:</strong> ${order.comment || ''}</p>
        <p><strong>Produkter / Products:</strong></p>
        <ul>${order.products.map(p => `<li>${p.amount} x ${p.product}</li>`).join('')}</ul>
        ${extra.total && order.totalIncVat ? `<p><strong>Totalt / Total:</strong> ${order.totalIncVat} kr inkl. mva / NOK incl. VAT</p>` : ''}
        <p><strong>Denne e-posten er sendt automatisk, vennligst ikke svar.<br>
        This email was sent automatically, please do not reply.</strong></p>`;
  }

  // Sent when a Frigg admin approves a customer's portal order. Deliberately
  // separate from orderConfirmationEmail: the customer needs to hear that their
  // request became a real order, in their own terms, not ours.
  approvedEmail(order: any) {
    const approvedEmail: Email = {
      emailTo: order.email,
      subject: this.bilingualSubject('Bekreftet', 'Confirmed', order, true),
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: this.bilingualHtml(order,
        `<p><strong>Møtematbestillingen din er bekreftet.</strong></p>`,
        `<p><strong>Your meeting food order is confirmed.</strong></p>`,
        { total: true }),
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      asap: true,
    };

    return this.emailService.sendEmail(approvedEmail);
  }

  rejectedEmail(order: any, reason: string) {
    const rejected: Email = {
      emailTo: order.email,
      subject: this.bilingualSubject('Ikke godkjent', 'Not approved', order, false),
      emailFrom: "stocking@cuboid.no",
      title: ``,
      html: this.bilingualHtml(order,
        `<p>Møtematbestillingen din kunne dessverre ikke godkjennes.</p>
         <p>Ta kontakt med Frigg hvis du trenger å avtale noe annet.</p>`,
        `<p>We are sorry — your meeting food order could not be approved.</p>
         <p>Please contact Frigg if you need to arrange something else.</p>`,
        { reason: reason || '' }),
      text: '',
      includeSignature: true,
      signatureType: 'frigg',
      asap: true,
    };

    return this.emailService.sendEmail(rejected);
  }
}
